import { createContext, useState, useCallback, useContext } from 'react';
import {resource_server } from "@utils/settings"
const ChatContext = createContext();


const API_BASE = resource_server+'/v1';

function ChatProvider({ children }) {
  const [messages, setMessages] = useState([]);
  const [chatVisible, setChatVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [currentSection, setCurrentSection] = useState('');

  const addMessage = useCallback((role, content, section) => {
    setMessages((prev) => [...prev, { role, content, section, id: Date.now() + Math.random() }]);
  }, []);

  const clearChat = useCallback(() => {
    setMessages([]);
  }, []);

  const toggleChat = useCallback(() => {
    setChatVisible((prev) => !prev);
  }, []);

  const sendMessage = useCallback(async (content, section) => {
    if (!content?.trim()) return;

    addMessage('user', content, section);
    setLoading(true);
    setStreamingContent('');

    const history = messages.map((m) => ({
      role: m.role,
      content: m.content,
    }));

    const userMessages = [
      ...(section
        ? [{ role: 'user', content: `[Sezione corrente: ${section}]` }]
        : []),
      ...history,
      { role: 'user', content },
    ];

    try {
      const response = await fetch(`${API_BASE}/chat/completions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: userMessages,
          stream: true,
        }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') continue;

          try {
            const parsed = JSON.parse(data);
            const delta = parsed?.choices?.[0]?.delta?.content || '';
            if (delta) {
              fullContent += delta;
              setStreamingContent(fullContent);
            }
          } catch {
            // skip malformed lines
          }
        }
      }

      const reply = fullContent || 'Mi dispiace, non ho ricevuto una risposta valida.';
      addMessage('assistant', reply);
    } catch (error) {
      addMessage('assistant', `Errore di connessione: ${error.message}. Verifica che il server Ollama sia in esecuzione su ${API_BASE}.`);
    } finally {
      setLoading(false);
      setStreamingContent('');
    }
  }, [addMessage, messages]);

  const isStreaming = streamingContent.length > 0;

  const value = {
    messages,
    chatVisible,
    setChatVisible,
    toggleChat,
    addMessage,
    clearChat,
    sendMessage,
    loading,
    streamingContent,
    isStreaming,
    currentSection,
    setCurrentSection,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
}

function useChat() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used within ChatProvider');
  return ctx;
}

export { ChatContext, ChatProvider, useChat };
